import { IShapefileNode } from "models/projects";

import { useStructuredNodeContext } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/StructuredNodeContext";
import { cancelNodeChanges } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/reducer";
import { useProjectDispatch } from "components/project/projects/context";
import { useDeleteProjectShapefileFolderMutation } from "components/project/shapefiles/mutations";

export type UseCancelShapefileFolderChangesParams = {
  node: IShapefileNode;
  projectId: string;
};

export type UseCancelShapefileFolderChangesReturn = {
  isRemoving: boolean;
  mutate: (e: React.SyntheticEvent) => void;
};

export function useCancelShapefileFolderChanges(
  params: UseCancelShapefileFolderChangesParams
): UseCancelShapefileFolderChangesReturn {
  const { node, projectId } = params;
  const projectDispatch = useProjectDispatch();
  const [{ isNewlyCreatedFolder, isRemoving }, nodeDispatch] = useStructuredNodeContext();

  const deleteProjectShapefileFolder = useDeleteProjectShapefileFolderMutation({
    projectId: projectId,
    onSuccess: () => {
      projectDispatch({ payload: { isTreeDraggable: true } });
    },
    onError: () => {
      projectDispatch({ payload: { isTreeDraggable: true } });
      nodeDispatch({ type: "setIsRemoving", payload: false });
    }
  });

  const mutate = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (isNewlyCreatedFolder) {
      nodeDispatch({ type: "setIsRemoving", payload: true });

      // Just letting the removal of the data take care of the isRemoving cleanup,
      // so there's no flicker of the transition
      deleteProjectShapefileFolder.mutate({
        shapefileNodeId: node.shapefileNodeId,
        projectId: projectId,
        // A newly created folder is empty, so this purges nothing today - but
        // the mutation's contract is "name what the cascade deletes".
        folderNode: node
      });
    } else {
      cancelNodeChanges(nodeDispatch, (isDraggable) =>
        projectDispatch({ payload: { isTreeDraggable: isDraggable } })
      );
    }
  };

  return { mutate, isRemoving };
}
